import React, { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import {
  ButtonBase,
  IconButton,
  LinearProgress,
} from "@material-ui/core";
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@material-ui/icons";

import { Button, Typography } from "../../components/Wrappers";
import useStyles, { SLOT_HEIGHT } from "./styles";
import {
  VIEW_DAY,
  VIEW_WEEK,
  VIEW_MONTH,
  DAY_END_MINUTES,
  SLOT_MINUTES,
  appointmentsForDay,
  canShift,
  clampDate,
  describeAppointment,
  formatAppointmentTimeRange,
  formatAppointmentTypeName,
  formatDayHeader,
  formatDuration,
  formatHandlerName,
  formatHouseholdName,
  formatMinutes,
  formatRangeLabel,
  hasAppointmentOverlap,
  holidayLabel,
  holidaysByDate,
  isCholHaMoedDay,
  isWorkForbiddenDay,
  layoutDay,
  navigationBounds,
  monthWeeks,
  shiftAnchor,
  timeSlots,
  toDateOnly,
  toLocalDate,
  toMinutes,
  visibleRange,
  workForbiddenReason,
} from "./calendarHelpers";
import { useJewishHolidays } from "../../apis";

// Empty business-hours configuration intentionally means unrestricted
// scheduling. Otherwise, a selected appointment must fit entirely inside one
// configured window for its local weekday; adjacent gaps remain unavailable.
const slotFitsBusinessHours = (businessHours, dateOnly, startMinutes, durationMinutes) => {
  const windows = (Array.isArray(businessHours) ? businessHours : [])
    .map(window => ({
      dayOfWeek: Number(window && window.dayOfWeek),
      startMinutes: toMinutes(window && window.startTime),
      endMinutes: toMinutes(window && window.endTime),
    }))
    .filter(window =>
      Number.isInteger(window.dayOfWeek) &&
      window.dayOfWeek >= 0 &&
      window.dayOfWeek <= 6 &&
      window.startMinutes !== null &&
      window.endMinutes !== null &&
      window.endMinutes > window.startMinutes,
    );
  if (!windows.length) return true;

  const day = toLocalDate(dateOnly);
  const endMinutes = startMinutes + durationMinutes;
  return !!day && windows.some(window =>
    window.dayOfWeek === day.getDay() &&
    startMinutes >= window.startMinutes &&
    endMinutes <= window.endMinutes,
  );
};

/**
 * AppointmentCalendar - the one calendar used by BOTH appointment consumers
 * (Requirements 9.1, 9.2, 9.4, 9.5, 10.2).
 *
 * PRESENTATIONAL ONLY: it never fetches. Its consumers own the data and the
 * view state, which is what lets the same component serve two very different
 * screens:
 *
 *   Appointments list page (task 11.3) - all handlers, day/week/month toggle,
 *   click to open the detail/edit form:
 *     const range = visibleRange(view, anchor)                 // from ./calendarHelpers
 *     const {appointments,isLoading,isError,mutate} = useAppointments({handler,appointmentType,from:range.start,to:range.end})
 *     <AppointmentCalendar
 *        appointments={appointments} view={view}
 *        anchorDate={anchor} onAnchorDateChange={setAnchor}
 *        onAppointmentClick={openAppointment} showHandler
 *        isLoading={isLoading} isError={isError} onRetry={mutate} />
 *
 *   Handler sidebar in the appointment form (task 11.4) - one handler, week
 *   only; pass `slotSelection` to allow a scheduled form to choose an available
 *   date/time, or omit it for read-only display:
 *     const {appointments,isLoading,isError,mutate} = useHandlerWeekAppointments(handlerId,anchor)
 *     <AppointmentCalendar appointments={appointments} anchorDate={anchor}
 *        onAnchorDateChange={setAnchor} isLoading={isLoading} isError={isError} onRetry={mutate} />
 *
 * PROPS
 *  appointments  array   Rows exactly as the api returns them. `date` may be a
 *                        'YYYY-MM-DD' string or the ISO timestamp a mysql DATE
 *                        becomes over JSON; `startTime` is a TIME ('HH:MM:SS');
 *                        `durationMinutes` a number. Relations are OPTIONAL and
 *                        endpoint dependent (the week endpoint has no `handler`),
 *                        so a missing one only blanks a line, never crashes.
 *                        Default [].
 *  view          string  'day' | 'week' | 'month' (Requirement 10.2). Default
 *                        'week' (Requirement 9.1). The TOGGLE ITSELF BELONGS TO
 *                        THE CONSUMER - this component only renders what it is
 *                        told to.
 *  anchorDate    Date|string  ANY date inside the range to show; the component
 *                        snaps it to the week/month like the server does. Omit
 *                        to start on today. Clamped into the navigable window.
 *  onAnchorDateChange(dateOnly)  Called with a 'YYYY-MM-DD' string when the user
 *                        navigates. Optional: without it the calendar still
 *                        navigates using internal state, but the consumer will
 *                        not know which range to fetch - pass it whenever the
 *                        data is range scoped (both real consumers do).
 *  onAppointmentClick(appointment)  Optional (Requirement 10.5). When given,
 *                        blocks render as real buttons (mouse + keyboard);
 *                        when omitted they are inert read-only blocks.
 *  showHandler   bool    Add the handler name to the block. Only useful where the
 *                        rows carry the `handler` relation, i.e. the list page.
 *  isLoading     bool    Renders a progress bar; the grid stays visible.
 *  isError       any     Truthy renders the Requirement 9.6 / 10.6 error state.
 *  onRetry       fn      Wire to the SWR `mutate` to make the retry button work.
 *  emptyMessage  string  Overrides the Requirement 9.5 / 10.6 empty message.
 *  title         string  Optional heading in the toolbar.
 *  hideNavigation bool   Hides the week/day/month stepper (bounds still apply).
 *  today         Date    Injectable "now" for the bounds and the today highlight.
 *  bounds        object   Overrides {minDate,maxDate} from navigationBounds().
 *  slotSelection object   EXPLICITLY opts in to selectable 30-minute slots for
 *                        day/week grids. Supply {durationMinutes, selectedSlot,
 *                        onSelect, minDate?, excludedAppointmentId?, disabled?,
 *                        disabledMessage?}; `onSelect({date,startMinutes})` is
 *                        called only for an in-grid, non-overlapping,
 *                        non-cancelled-appointment interval. `excludedAppointmentId`
 *                        omits exactly that appointment from client-side conflict
 *                        checks, for a scheduled appointment being edited.
 *                        Without this object, the grid remains read-only.
 *  className     string  Appended to the root element's classes.
 *
 * NOT ITS JOB: fetching, the view toggle, the filter controls, the detail panel,
 * or owning form state. Opt-in selection merely reports a valid slot to its
 * consumer; the backend remains authoritative for stale/concurrent conflicts.
 */
const AppointmentCalendar = ({
  appointments = [],
  view = VIEW_WEEK,
  anchorDate,
  onAnchorDateChange,
  onAppointmentClick,
  showHandler = false,
  isLoading = false,
  isError = null,
  onRetry,
  emptyMessage,
  title,
  hideNavigation = false,
  today,
  bounds: boundsProp,
  businessHours: businessHoursProp,
  businessHoursLoading: businessHoursLoadingProp,
  businessHoursError: businessHoursErrorProp,
  slotSelection,
  className,
}) => {
  const classes = useStyles();

  const activeView = view === VIEW_DAY || view === VIEW_MONTH ? view : VIEW_WEEK;
  //`today` is a prop so the bounds and the highlight can be pinned in a test
  //merged rather than replaced, so a consumer can override just minDate/maxDate
  //and still get a `today` for the highlight
  const bounds = useMemo(() => ({ ...navigationBounds(today), ...(boundsProp || {}) }), [boundsProp, today]);
  const anchorProp = toDateOnly(anchorDate);

  // The anchor lives here so the calendar navigates on its own when the consumer
  // does not care, while a consumer that passes `anchorDate` stays the source of
  // truth. An anchor outside the Requirement 9.4 window is clamped rather than
  // rendered, which also keeps the stepper from dead-ending outside the window.
  const [anchor, setAnchor] = useState(() => clampDate(anchorProp || bounds.today, bounds));
  //the functional update matters: with no `anchorDate` prop the user's own
  //navigation is kept (and merely re-clamped), instead of being reset every time
  //the parent re-renders
  useEffect(() => {
    setAnchor(current => clampDate(anchorProp || current || bounds.today, bounds));
  }, [anchorProp, bounds]);

  const range = useMemo(() => visibleRange(activeView, anchor), [activeView, anchor]);
  const slots = useMemo(() => timeSlots(), []);

  // Slot selection is deliberately opt-in so the existing list and read-only
  // sidebar consumers retain their exact behavior. The calendar owns visual
  // availability; the form owns the selected values and server-side validation
  // remains the authority for concurrent changes.
  // The Jewish calendar for the range on screen. Fetched here rather than passed
  // in so every consumer of this grid gets it without extra wiring; SWR dedupes
  // the request with the one the appointment form makes for its date picker.
  //
  // Yom Tov blocks a day out; Chol HaMoed is only styled and stays selectable.
  const { holidays } = useJewishHolidays(range.start, range.end);
  const holidayIndex = useMemo(() => holidaysByDate(holidays), [holidays]);

  const slotSelectionEnabled =
    !!slotSelection && typeof slotSelection.onSelect === "function" && activeView !== VIEW_MONTH;
  const selectionDuration = Number(slotSelection && slotSelection.durationMinutes);
  const hasSelectionDuration = Number.isFinite(selectionDuration) && selectionDuration > 0;
  const selectedSlot = (slotSelection && slotSelection.selectedSlot) || {};
  const selectedSlotDate = toDateOnly(selectedSlot.date);
  const selectedSlotStartMinutes = toMinutes(selectedSlot.startTime);
  const selectionMinDate = toDateOnly(slotSelection && slotSelection.minDate);
  const excludedAppointmentId = slotSelection && slotSelection.excludedAppointmentId;
  const selectionDataUnavailable = isLoading || !!isError;
  const businessHoursLoading =
    businessHoursLoadingProp !== undefined
      ? !!businessHoursLoadingProp
      : !!(slotSelection && slotSelection.businessHoursLoading);
  const businessHoursError =
    businessHoursErrorProp !== undefined
      ? !!businessHoursErrorProp
      : !!(slotSelection && slotSelection.businessHoursError);
  const businessHoursDataUnavailable = businessHoursLoading || businessHoursError;
  const businessHours =
    businessHoursProp !== undefined ? businessHoursProp : slotSelection && slotSelection.businessHours;
  const selectionDisabled = !!(slotSelection && slotSelection.disabled);
  const selectionDisabledMessage =
    (slotSelection && slotSelection.disabledMessage) || "Time slots cannot be selected right now.";
  const slotSelectionStatus = !slotSelectionEnabled
    ? ""
    : !hasSelectionDuration
    ? "Choose a duration before selecting a calendar time slot."
    : isLoading
    ? "Handler appointments are loading. Time slots cannot be selected yet."
    : isError
    ? "Handler appointments could not be retrieved. Time slots cannot be selected until loading succeeds."
    : businessHoursLoading
    ? "Business hours are loading. Time slots cannot be selected yet."
    : businessHoursError
    ? "Business hours could not be retrieved. Time slots cannot be selected until loading succeeds."
    : selectionDisabled
    ? selectionDisabledMessage
    : "Choose an available date and start time. The server will confirm availability when the appointment is saved.";

  const getSlotAvailability = (day, slot) => {
    const dateAllowed = !selectionMinDate || day >= selectionMinDate;
    const runsPastGridEnd = hasSelectionDuration && slot.minutes + selectionDuration > DAY_END_MINUTES;
    // A Jewish holiday work is not permitted on takes the WHOLE day out, so it is
    // tested per day rather than per slot. The api refuses these too (400), which
    // is why the grid must not offer them: a slot the server will reject is a trap.
    const holidayBlocked = isWorkForbiddenDay(holidayIndex, day);
    const withinBusinessHours =
      hasSelectionDuration &&
      !businessHoursDataUnavailable &&
      slotFitsBusinessHours(businessHours, day, slot.minutes, selectionDuration);
    const conflicts =
      hasSelectionDuration &&
      !selectionDataUnavailable &&
      !businessHoursDataUnavailable &&
      !selectionDisabled &&
      dateAllowed &&
      !holidayBlocked &&
      !runsPastGridEnd &&
      withinBusinessHours &&
      hasAppointmentOverlap(appointments, day, slot.minutes, selectionDuration, excludedAppointmentId);
    const available =
      slotSelectionEnabled &&
      hasSelectionDuration &&
      !selectionDataUnavailable &&
      !businessHoursDataUnavailable &&
      !selectionDisabled &&
      dateAllowed &&
      !holidayBlocked &&
      !runsPastGridEnd &&
      withinBusinessHours &&
      !conflicts;
    const selected = available && selectedSlotDate === day && selectedSlotStartMinutes === slot.minutes;

    let unavailableReason = "";
    if (!hasSelectionDuration) unavailableReason = "Choose a duration first";
    else if (isLoading) unavailableReason = "Handler appointments are still loading";
    else if (isError) unavailableReason = "Handler appointments could not be retrieved";
    else if (businessHoursLoading) unavailableReason = "Business hours are still loading";
    else if (businessHoursError) unavailableReason = "Business hours could not be retrieved";
    else if (selectionDisabled) unavailableReason = selectionDisabledMessage;
    else if (!dateAllowed) unavailableReason = "Past dates cannot be selected";
    //names the holiday rather than saying only "unavailable"
    else if (holidayBlocked) unavailableReason = workForbiddenReason(holidayIndex, day);
    else if (runsPastGridEnd) unavailableReason = "This duration would run past 8:00 PM";
    else if (!withinBusinessHours) unavailableReason = "This interval is outside configured business hours";
    else if (conflicts) unavailableReason = "This interval overlaps an existing appointment";

    return { available, selected, unavailableReason };
  };

  //greys out the read-only grid too, so a blocked day looks blocked before a
  //duration has even been chosen
  const isReadOnlySlotUnavailable = (day, slot) =>
    isWorkForbiddenDay(holidayIndex, day) ||
    (!businessHoursDataUnavailable &&
      !slotFitsBusinessHours(businessHours, day, slot.minutes, SLOT_MINUTES));

  const renderSlotControl = (day, slot) => {
    const availability = getSlotAvailability(day, slot);
    const dayLabel = formatRangeLabel({ view: VIEW_DAY, start: day, end: day });
    const durationLabel = formatDuration(selectionDuration);
    const ariaLabel = availability.available
      ? `${availability.selected ? "Selected" : "Select"} ${dayLabel} at ${slot.label} for ${durationLabel}`
      : `Unavailable: ${dayLabel} at ${slot.label}. ${availability.unavailableReason}.`;

    return (
      <ButtonBase
        key={slot.minutes}
        component="button"
        type="button"
        disabled={!availability.available}
        onClick={() => slotSelection.onSelect({ date: day, startMinutes: slot.minutes })}
        aria-label={ariaLabel}
        aria-pressed={availability.available ? availability.selected : undefined}
        className={classnames(classes.slotRow, classes.slotButton, {
          [classes.slotRowHour]: slot.isHour,
          [classes.slotButtonUnavailable]: !availability.available,
          [classes.slotButtonSelected]: availability.selected,
        })}
      />
    );
  };

  //laid out once per data/range change, and reused by both the columns and the
  //out-of-hours list underneath them
  const dayLayouts = useMemo(
    () => range.days.map(day => ({ day, ...layoutDay(appointments, day) })),
    [appointments, range]
  );
  const outsideAppointments = useMemo(
    () => dayLayouts.reduce((all, layout) => all.concat(layout.outside), []),
    [dayLayouts]
  );

  //Requirement 9.5 / 10.6 - the grid is rendered either way, this only decides
  //whether the "nothing scheduled" message goes with it
  const visibleCount = useMemo(
    () => range.days.reduce((total, day) => total + appointmentsForDay(appointments, day).length, 0),
    [appointments, range]
  );

  const step = direction => {
    const next = shiftAnchor(activeView, anchor, direction);
    if (!next) return;
    const clamped = clampDate(next, bounds);
    setAnchor(clamped);
    if (onAnchorDateChange) onAnchorDateChange(clamped);
  };

  const goToday = () => {
    const clamped = clampDate(bounds.today, bounds);
    setAnchor(clamped);
    if (onAnchorDateChange) onAnchorDateChange(clamped);
  };

  const unit = activeView === VIEW_DAY ? "day" : activeView === VIEW_MONTH ? "month" : "week";
  const rangeLabel = formatRangeLabel(range);

  //Requirement 9.4 - at a bound the control is DISABLED, not silently ignored
  const canGoBack = canShift(activeView, anchor, -1, bounds);
  const canGoForward = canShift(activeView, anchor, 1, bounds);

  const renderBlockLines = appointment => {
    const lines = [
      //Requirement 9.2 - time, duration, type and household name on the block
      `${formatMinutes(appointment.startTime)} \u00b7 ${formatDuration(appointment.durationMinutes)}`,
      formatAppointmentTypeName(appointment),
      formatHouseholdName(appointment.household) || `Household #${appointment.householdId}`,
    ];
    //Requirement 10.3 adds the handler, but only the list endpoint sends it
    if (showHandler) lines.push(formatHandlerName(appointment.handler));
    //never colour alone: a cancelled appointment says so in words
    if (appointment.status && appointment.status !== "scheduled") lines.push(appointment.status);
    return lines.filter(Boolean).map((line, index) => (
      <span key={index} className={classes.appointmentBlockText}>
        {line}
      </span>
    ));
  };

  // Requirement 9.2 - a block SPANS ITS DURATION: `topSlots` / `spanSlots` come
  // back from layoutDay in 30 minute units and are multiplied by the shared
  // SLOT_HEIGHT, so a 60 minute appointment covers exactly two rows. Overlapping
  // appointments (possible across handlers on the list page) share the column
  // width via columnIndex / columnCount.
  const renderBlock = block => {
    const { appointment } = block;
    const cancelled = appointment.status && appointment.status !== "scheduled";
    const blockStyle = {
      top: block.topSlots * SLOT_HEIGHT,
      height: Math.max(block.spanSlots * SLOT_HEIGHT - 2, 16),
      left: `${(block.columnIndex / block.columnCount) * 100}%`,
      width: `calc(${100 / block.columnCount}% - 3px)`,
    };
    const description = describeAppointment(appointment, { includeHandler: showHandler });
    const blockClasses = classnames(classes.appointmentBlock, {
      [classes.appointmentBlockClickable]: !!onAppointmentClick,
      [classes.appointmentBlockCancelled]: cancelled,
    });

    return (
      <li key={appointment.id} className={classes.appointmentBlockItem} style={blockStyle}>
        {onAppointmentClick ? (
          //ButtonBase is a real <button>: mouse, Enter and Space all activate it
          <ButtonBase
            className={classnames(blockClasses, classes.appointmentBlockFill)}
            onClick={() => onAppointmentClick(appointment)}
            aria-label={description}
          >
            <span className={classes.appointmentBlockContent}>{renderBlockLines(appointment)}</span>
          </ButtonBase>
        ) : (
          <div className={classnames(blockClasses, classes.appointmentBlockFill)} title={description}>
            <span className={classes.appointmentBlockContent}>{renderBlockLines(appointment)}</span>
          </div>
        )}
      </li>
    );
  };

  // Requirement 9.1 - 30 minute rows from 8:00 AM to 8:00 PM. They remain
  // decorative for read-only consumers. Opt-in selection instead renders a real
  // button for every row; appointment blocks stay inert unless onAppointmentClick
  // was explicitly supplied, so blocks themselves never become slot controls.
  const renderDayColumn = ({ day, blocks, outside }) => {
    const header = formatDayHeader(day);
    const count = blocks.length + outside.length;
    return (
      <div key={day} className={classes.dayColumn}>
        {slotSelectionEnabled ? (
          slots.map(slot => renderSlotControl(day, slot))
        ) : (
          <div aria-hidden="true">
            {slots.map(slot => (
              <div
                key={slot.minutes}
                className={classnames(classes.slotRow, {
                  [classes.slotRowHour]: slot.isHour,
                  [classes.slotRowUnavailable]: isReadOnlySlotUnavailable(day, slot),
                })}
              />
            ))}
          </div>
        )}
        <ul
          className={classes.appointmentLayer}
          aria-label={`${header.weekday} ${header.day}: ${count} appointment${count === 1 ? "" : "s"}`}
        >
          {blocks.map(renderBlock)}
        </ul>
      </div>
    );
  };

  const renderTimeGrid = () => (
    <div className={classes.calendarScroll}>
      <div className={classnames(classes.calendarGrid, { [classes.calendarGridDay]: activeView === VIEW_DAY })}>
        <div className={classes.calendarHeaderRow}>
          <div className={classes.timeGutter} aria-hidden="true" />
          {range.days.map(day => {
            const header = formatDayHeader(day);
            const holiday = holidayLabel(holidayIndex, day);
            const blocked = isWorkForbiddenDay(holidayIndex, day);
            return (
              <div
                key={day}
                className={classnames(classes.dayColumnHeader, {
                  [classes.dayColumnHeaderToday]: day === bounds.today,
                  [classes.dayYomTov]: blocked,
                  [classes.dayCholHaMoed]: isCholHaMoedDay(holidayIndex, day),
                })}
              >
                <Typography variant="caption" display="block">
                  {header.weekday}
                </Typography>
                <Typography variant="body2">{header.day}</Typography>
                {/* Week columns are narrow, so the name is truncated by
                    `holidayLabel` styling and the full text plus the reason live in
                    `title` - the tooltip and the accessible name at once. Never
                    colour alone: the holiday is always named in text. */}
                {holiday ? (
                  <span
                    className={classnames(classes.holidayLabel, {
                      [classes.holidayLabelYomTov]: blocked,
                      [classes.holidayLabelCholHaMoed]: isCholHaMoedDay(holidayIndex, day),
                    })}
                    title={blocked ? workForbiddenReason(holidayIndex, day) : holiday}
                  >
                    {holiday}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className={classes.calendarBodyRow}>
          <div className={classes.timeGutter}>
            {slots.map(slot => (
              <div key={slot.minutes} className={classes.timeGutterSlot}>
                {/* only the on-the-hour rows are labelled, so the gutter stays readable */}
                {slot.isHour && <Typography variant="caption">{slot.label}</Typography>}
              </div>
            ))}
          </div>
          {dayLayouts.map(renderDayColumn)}
        </div>
      </div>
    </div>
  );

  // An appointment starting before 8:00 AM or after 8:00 PM (or with unreadable
  // times) has no row in the Requirement 9.1 grid. Listing it under the grid
  // keeps the column heights aligned and, more importantly, means data is never
  // silently dropped from the view.
  const renderOutsideHours = () => (
    <ul className={classes.outsideGridList} aria-label="Appointments outside calendar hours">
      {outsideAppointments.map(({ appointment }) => (
        <li key={appointment.id}>
          <Typography variant="caption" color="secondary">
            {`${formatRangeLabel({ view: VIEW_DAY, start: appointment.date, end: appointment.date })} \u00b7 ${
              formatAppointmentTimeRange(appointment) || "time unavailable"
            } \u00b7 ${formatAppointmentTypeName(appointment)} \u00b7 ${formatHouseholdName(appointment.household)} (outside calendar hours)`}
          </Typography>
        </li>
      ))}
    </ul>
  );

  // Requirement 10.2's month view is a summary: a month of 30 minute rows would
  // be unreadable, so each day lists its appointments compactly instead. The
  // 8:00 AM - 8:00 PM grid (Requirement 9.1) stays the day and week views' job.
  const renderMonthGrid = () => (
    <div className={classes.calendarScroll}>
      <div className={classes.calendarGrid}>
        <div className={classes.calendarHeaderRow}>
          {(range.days.slice(0, 7) || []).map(day => (
            <div key={`head-${day}`} className={classes.dayColumnHeader}>
              <Typography variant="caption">{formatDayHeader(day).weekday}</Typography>
            </div>
          ))}
        </div>
        {monthWeeks(range).map(week => (
          <div key={`week-${week[0]}`} className={classes.monthWeekRow}>
            {week.map(day => {
              const dayAppointments = appointmentsForDay(appointments, day);
              const outsideMonth = day < range.monthStart || day > range.monthEnd;
              const header = formatDayHeader(day);
              //leading/trailing days belong to another month and are already dimmed
              const holiday = outsideMonth ? "" : holidayLabel(holidayIndex, day);
              const blocked = !outsideMonth && isWorkForbiddenDay(holidayIndex, day);
              return (
                <div
                  key={day}
                  className={classnames(classes.monthDayCell, {
                    [classes.monthDayCellOutside]: outsideMonth,
                    [classes.dayYomTov]: blocked,
                    [classes.dayCholHaMoed]: !outsideMonth && isCholHaMoedDay(holidayIndex, day),
                  })}
                >
                  <Typography variant="caption" className={classes.monthDayNumber}>
                    {header.day}
                  </Typography>
                  {/* A month cell has room for the name; it still truncates, and
                      `title` carries the full text and the reason it is blocked. */}
                  {holiday ? (
                    <span
                      className={classnames(classes.holidayLabel, {
                        [classes.holidayLabelYomTov]: blocked,
                        [classes.holidayLabelCholHaMoed]: isCholHaMoedDay(holidayIndex, day),
                      })}
                      title={blocked ? workForbiddenReason(holidayIndex, day) : holiday}
                    >
                      {holiday}
                    </span>
                  ) : null}
                  <ul
                    className={classes.monthEntryList}
                    aria-label={`${header.weekday} ${header.day}: ${dayAppointments.length} appointment${
                      dayAppointments.length === 1 ? "" : "s"
                    }`}
                  >
                    {dayAppointments.map(appointment => {
                      const description = describeAppointment(appointment, { includeHandler: showHandler });
                      const label = `${formatMinutes(appointment.startTime)} ${formatAppointmentTypeName(
                        appointment
                      )} ${formatHouseholdName(appointment.household)}`.trim();
                      return (
                        <li key={appointment.id}>
                          {onAppointmentClick ? (
                            <ButtonBase
                              className={classes.monthEntry}
                              onClick={() => onAppointmentClick(appointment)}
                              aria-label={description}
                            >
                              <Typography variant="caption">{label}</Typography>
                            </ButtonBase>
                          ) : (
                            <div className={classes.monthEntry} title={description}>
                              <Typography variant="caption">{label}</Typography>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={className}>
      <div className={classes.calendarToolbar}>
        {!!title && (
          <Typography variant="h6" color="text" colorBrightness="secondary">
            {title}
          </Typography>
        )}
        {!hideNavigation && (
          <>
            {/* Requirement 9.4 - one step at a time, disabled at the window edge */}
            <IconButton
              size="small"
              onClick={() => step(-1)}
              disabled={!canGoBack}
              aria-label={`Previous ${unit}`}
            >
              <PrevIcon />
            </IconButton>
            <IconButton size="small" onClick={() => step(1)} disabled={!canGoForward} aria-label={`Next ${unit}`}>
              <NextIcon />
            </IconButton>
            <Button size="small" onClick={goToday} aria-label="Go to today">
              Today
            </Button>
          </>
        )}
        <Typography variant="subtitle1" className={classes.calendarRangeLabel} aria-live="polite">
          {rangeLabel}
        </Typography>
        <div className={classes.calendarToolbarSpacer} />
      </div>

      {/* the bar sits above the grid rather than replacing it, so navigating does
          not make the calendar disappear and jump the page around */}
      {!!isLoading && <LinearProgress aria-label="Loading appointments" />}

      {!!slotSelectionEnabled && (
        <div className={classes.slotSelectionStatus} role="status" aria-live="polite">
          <Typography variant="body2" color="text" colorBrightness="secondary">
            {slotSelectionStatus}
          </Typography>
        </div>
      )}

      {/* Requirements 9.6 / 10.6 - say it failed and offer the retry */}
      {!!isError && (
        <div className={classes.calendarMessage} role="alert">
          <Typography variant="body2" color="secondary">
            The appointments for this {unit} could not be retrieved.
          </Typography>
          {!!onRetry && (
            //called with NO arguments: `onRetry` is an SWR `mutate`, and handing it
            //the click event would write that event into the cache as the data
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => onRetry()}
              className={classes.margin}
            >
              Retry
            </Button>
          )}
        </div>
      )}

      {activeView === VIEW_MONTH ? renderMonthGrid() : renderTimeGrid()}

      {activeView !== VIEW_MONTH && !!outsideAppointments.length && renderOutsideHours()}

      {/* Requirement 9.5 - the empty grid above STAYS, this only adds the message */}
      {!isLoading && !isError && !visibleCount && (
        <div className={classes.calendarMessage}>
          <Typography variant="body2" color="text" colorBrightness="secondary">
            {emptyMessage || `No existing appointments for this ${unit}.`}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;
